import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, ChevronLeft, ChevronRight, Plus, Copy, Clipboard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Doctor, Staff, Room, ScheduleItem } from './SchedulerTypes';
import { useClinic } from '@/contexts/ClinicContext';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// ScheduleData interface for type safety
interface ScheduleData {
  title: string;
  type: 'appointment' | 'admin' | 'break' | 'leave' | 'mc';
  doctorId?: string;
  staffId?: string;
  roomId?: string;
  startTime: string;
  endTime: string;
  notes?: string;
  date: string;
  endDate?: string;
}
import { ViewType } from './Scheduler';
import DoctorQuickReportModal from './DoctorQuickReportModal';
import StaffQuickReportModal from './StaffQuickReportModal';
import ScheduleModal from './ScheduleModal';
import { Reassignment } from './ConflictResolutionModal';
import { getCalendarDays, formatDateToString, isToday, toSingaporeTime, getCurrentSingaporeDate, expandMultiDaySchedules } from '../lib/dateUtils';
import PlanningMonthlyView from './PlanningMonthlyView';
import LoadingOverlay from './LoadingOverlay';

interface MonthlyViewProps {
  currentDate: Date;
  onDateChange: (date: Date) => void;
  onViewChange: (view: ViewType) => void;
  scheduleItems: ScheduleItem[];
  doctors: Doctor[];
  staff: Staff[];
  rooms: Room[];
  initialTab?: string;
  onAddScheduleItem: (item: Omit<ScheduleItem, 'id' | 'created_at' | 'updated_at'>, reassignments?: Reassignment[]) => void;
  onUpdateScheduleItem: (id: string, updates: Partial<ScheduleItem>) => void;
  onCopyScheduleItems: (date: string) => void;
  onPasteScheduleItems: (date: string) => void;
  copiedItems: ScheduleItem[];
  onDeleteScheduleItem: (id: string) => void;
  onDeleteScheduleSeries?: (ids: string[]) => void;
  isLoadingSchedules?: boolean;
  isFetchingSchedules?: boolean;
}

export default function MonthlyView({
  currentDate,
  onDateChange,
  onViewChange,
  scheduleItems,
  doctors,
  staff,
  rooms,
  initialTab,
  onAddScheduleItem,
  onUpdateScheduleItem,
  onCopyScheduleItems,
  onPasteScheduleItems,
  copiedItems,
  onDeleteScheduleItem,
  onDeleteScheduleSeries,
  isLoadingSchedules = false,
  isFetchingSchedules = false
}: MonthlyViewProps) {
  const navigate = useNavigate();
  const { currentClinic } = useClinic();
  const isAllClinicsView = !currentClinic;
  
  // Debug logging
  React.useEffect(() => {
    console.log('[MonthlyView] Component rendered with:');
    console.log('[MonthlyView] scheduleItems:', scheduleItems?.length || 0);
    console.log('[MonthlyView] currentClinic:', currentClinic?.name || 'None');
    console.log('[MonthlyView] currentDate:', currentDate);
    if (scheduleItems && scheduleItems.length > 0) {
      console.log('[MonthlyView] Sample item:', scheduleItems[0]);
    }
  }, [scheduleItems, currentClinic, currentDate]);
  
  // Map URL-friendly tab names to actual tab values
  const tabMapping: { [key: string]: string } = {
    'planning': 'planning',
    'all-schedule': 'all',
    'doctors': 'doctors',
    'staff': 'staff'
  };

  // Determine the default tab from the URL or props
  const getDefaultTab = () => {
    if (initialTab && tabMapping[initialTab]) {
      return tabMapping[initialTab];
    }
    return 'planning';
  };
  
  const [activeTab, setActiveTab] = useState(getDefaultTab());
  const [isTabLoading, setIsTabLoading] = useState(false);

  // Update tab when URL changes
  useEffect(() => {
    if (initialTab && tabMapping[initialTab]) {
      setActiveTab(tabMapping[initialTab]);
    }
  }, [initialTab]);

  // Handle tab change
  const handleTabChange = (value: string) => {
    setIsTabLoading(true);
    setActiveTab(value);
    // Update URL with the new tab
    const reverseMapping: { [key: string]: string } = {
      'planning': 'planning',
      'all': 'all-schedule',
      'doctors': 'doctors',
      'staff': 'staff'
    };
    navigate(`/monthly/${reverseMapping[value]}`);

    // Clear loading after render completes
    setTimeout(() => setIsTabLoading(false), 300);
  };

  // Doctor Quick Report Modal state
  const [doctorModalState, setDoctorModalState] = useState<{
    isOpen: boolean;
    scheduleItem: ScheduleItem | null;
    doctor: Doctor | null;
    staff: Staff | null;
    room: Room | null;
    allSchedulesForDay?: ScheduleItem[];
  }>({
    isOpen: false,
    scheduleItem: null,
    doctor: null,
    staff: null,
    room: null,
    allSchedulesForDay: []
  });

  // Staff Quick Report Modal state
  const [staffModalState, setStaffModalState] = useState<{
    isOpen: boolean;
    scheduleItem: ScheduleItem | null;
    staff: Staff | null;
    doctor: Doctor | null;
    room: Room | null;
    allSchedulesForDay?: ScheduleItem[];
  }>({
    isOpen: false,
    scheduleItem: null,
    staff: null,
    doctor: null,
    room: null,
    allSchedulesForDay: []
  });

  // Schedule Modal state
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [editingScheduleItem, setEditingScheduleItem] = useState<ScheduleItem | null>(null);

  // Helper functions
  const getDoctorById = (doctorId: string) => doctors.find(d => d.id === doctorId);
  const getStaffById = (staffId: string) => staff.find(s => s.id === staffId);
  const getRoomById = (roomId: string) => rooms.find(r => r.id === roomId);

  const handleDoctorClick = (scheduleItem: ScheduleItem, e: React.MouseEvent, allSchedulesForDay?: ScheduleItem[]) => {
    e.stopPropagation();
    
    const doctor = scheduleItem.doctorId ? getDoctorById(scheduleItem.doctorId) : null;
    const staffMember = scheduleItem.staffId ? getStaffById(scheduleItem.staffId) : null;
    const room = scheduleItem.roomId ? getRoomById(scheduleItem.roomId) : null;

    if (doctor) {
      setDoctorModalState({
        isOpen: true,
        scheduleItem,
        doctor,
        staff: staffMember || null,
        room: room || null,
        allSchedulesForDay: allSchedulesForDay || [scheduleItem]
      });
    }
  };

  const handleStaffClick = (scheduleItem: ScheduleItem, e: React.MouseEvent, allSchedulesForDay?: ScheduleItem[]) => {
    e.stopPropagation();
    
    const staffMember = scheduleItem.staffId ? getStaffById(scheduleItem.staffId) : null;
    const doctor = scheduleItem.doctorId ? getDoctorById(scheduleItem.doctorId) : null;
    const room = scheduleItem.roomId ? getRoomById(scheduleItem.roomId) : null;

    if (staffMember) {
      setStaffModalState({
        isOpen: true,
        scheduleItem,
        staff: staffMember,
        doctor: doctor || null,
        room: room || null,
        allSchedulesForDay: allSchedulesForDay || [scheduleItem]
      });
    }
  };

  const closeDoctorModal = () => {
    setDoctorModalState({
      isOpen: false,
      scheduleItem: null,
      doctor: null,
      staff: null,
      room: null,
      allSchedulesForDay: []
    });
  };

  const closeStaffModal = () => {
    setStaffModalState({
      isOpen: false,
      scheduleItem: null,
      staff: null,
      doctor: null,
      room: null,
      allSchedulesForDay: []
    });
  };

  const handleDeleteSchedule = (scheduleId: string) => {
    // Extract base UUID from composite IDs (removes _day_X suffix)
    const baseId = scheduleId.split('_day_')[0];
    onDeleteScheduleItem(baseId);
    closeDoctorModal();
    closeStaffModal();
  };

  const handleDeleteSeries = (ids: string[]) => {
    onDeleteScheduleSeries?.(ids);
    closeDoctorModal();
    closeStaffModal();
  };

  // Schedule Modal handlers
  const openScheduleModal = (item?: ScheduleItem) => {
    setEditingScheduleItem(item || null);
    setIsScheduleModalOpen(true);
  };

  const closeScheduleModal = () => {
    setIsScheduleModalOpen(false);
    setEditingScheduleItem(null);
  };

  const handleSaveSchedule = (scheduleData: ScheduleData, reassignments?: Reassignment[]) => {
    if (editingScheduleItem) {
      // Update existing schedule item - extract base UUID from composite IDs
      const baseId = editingScheduleItem.id.split('_day_')[0];
      onUpdateScheduleItem(baseId, scheduleData);
    } else {
      // Create new schedule item with today's date as default, potentially with reassignments
      const newScheduleData = {
        ...scheduleData,
        date: scheduleData.date || formatDateToString(getCurrentSingaporeDate())
      };
      onAddScheduleItem(newScheduleData, reassignments);
    }
    closeScheduleModal();
  };

  const handleEditSchedule = (scheduleItem: ScheduleItem) => {
    // Extract the base UUID from composite IDs (removes _day_X suffix)
    const baseId = scheduleItem.id.split('_day_')[0];
    
    // Find the original schedule item using the base ID
    const originalItem = scheduleItems.find(item => item.id === baseId) || scheduleItem;
    
    // Open the schedule modal with the original item for editing
    openScheduleModal(originalItem);
  };

  const handleDayClick = (day: Date) => {
    onDateChange(day);
    onViewChange('weekly');
  };

  // Calendar generation logic using timezone-aware utilities
  const singaporeDate = toSingaporeTime(currentDate);
  const year = singaporeDate.getFullYear();
  const month = singaporeDate.getMonth();
  
  const calendarDays = getCalendarDays(currentDate);
  
  // Expand multi-day schedules to show on all applicable days
  const expandedScheduleItems = useMemo(() => {
    if (calendarDays.length === 0) return [];
    const monthStart = formatDateToString(calendarDays[0].date);
    const monthEnd = formatDateToString(calendarDays[calendarDays.length - 1].date);
    const expanded = expandMultiDaySchedules(scheduleItems, monthStart, monthEnd);
    
    // Debug logging for expansion
    if (scheduleItems.length !== expanded.length) {
      console.log(`[MonthlyView] Schedule expansion:`, {
        original: scheduleItems.length,
        expanded: expanded.length,
        monthStart,
        monthEnd,
        multiDayItems: expanded.filter(item => item.isMultiDay).length
      });
    }
    
    return expanded;
  }, [scheduleItems, calendarDays]);
  
  const prevMonth = new Date(year, month - 1, 1);
  const nextMonth = new Date(year, month + 1, 1);

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = direction === 'prev' ? prevMonth : nextMonth;
    onDateChange(newDate);
  };

  // Get schedules for a day with deduplication
  const getSchedulesForDay = (dateString: string) => {
    const daySchedules = expandedScheduleItems.filter(item => item.date === dateString);
    
    // Debug logging for duplicate investigation
    const ids = daySchedules.map(item => item.id);
    const uniqueIds = new Set(ids);
    if (ids.length !== uniqueIds.size) {
      console.warn(`[MonthlyView] Found duplicate schedule IDs for ${dateString}:`, {
        total: ids.length,
        unique: uniqueIds.size,
        duplicateIds: ids.filter((id, index) => ids.indexOf(id) !== index),
        allSchedules: daySchedules
      });
    }
    
    // Additional deduplication by ID (safety net)
    const seenIds = new Set<string>();
    const deduplicated = daySchedules.filter(item => {
      if (seenIds.has(item.id)) {
        console.warn(`[MonthlyView] Removing duplicate item with ID: ${item.id}`);
        return false;
      }
      seenIds.add(item.id);
      return true;
    });
    
    return deduplicated;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const renderMonthlyContent = (viewType: 'all' | 'doctors' | 'staff') => (
    <div className="overflow-x-auto -mx-2 px-2" style={{ WebkitOverflowScrolling: 'touch' }}>
      <div className="min-w-[500px]">
      {/* Sticky day headers */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2 sticky top-[52px] md:top-[73px] z-30 bg-background pb-2 border-b">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((dayName) => (
          <div key={dayName} className="p-1 sm:p-2 text-center text-xs sm:text-sm font-medium text-muted-foreground bg-muted/50">
            {dayName}
          </div>
        ))}
      </div>
      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2 pt-2">

      {/* Calendar days */}
             {calendarDays.map((dayObj, index) => {
         const day = dayObj.date;
         const isCurrentMonth = dayObj.isCurrentMonth;
         const isTodayDate = isToday(day);
         const dateString = formatDateToString(day);
        
        // Get schedules for the day using lazy loading cache if available
        const allDaySchedules = getSchedulesForDay(dateString);
        
        let daySchedules: ScheduleItem[] = [];
        
        // Filter schedules based on view type
        if (viewType === 'all') {
          daySchedules = allDaySchedules;
        } else if (viewType === 'doctors') {
          daySchedules = allDaySchedules.filter(item => item.doctorId);
        } else if (viewType === 'staff') {
          daySchedules = allDaySchedules.filter(item => item.staffId);
        }
        
        // Sort schedules to move leave types to the bottom
        daySchedules.sort((a, b) => {
          // Priority order: appointments/admin/break > leave > mc (mc still last for urgency)
          const getPriority = (type: string) => {
            if (type === 'mc') return 3; // Most urgent, show last
            if (type === 'leave') return 2; // Leave types go to bottom
            return 1; // Normal schedules (appointment, admin, break) at top
          };
          
          const aPriority = getPriority(a.type);
          const bPriority = getPriority(b.type);
          
          if (aPriority !== bPriority) {
            return aPriority - bPriority;
          }
          
          // If same priority, sort by time
          return a.startTime.localeCompare(b.startTime);
        });
        
        // All items are now loaded completely, no need for lazy loading
        const visibleSchedules = daySchedules;

        return (
          <Card
            key={index}
            className={`h-[120px] sm:h-[150px] md:h-[180px] cursor-pointer transition-colors hover:bg-muted/50 ${
              !isCurrentMonth ? 'opacity-50' : ''
            } ${isTodayDate ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleDayClick(day)}
          >
            <CardHeader className="p-2">
              <div className="flex items-center justify-between">
                <CardTitle className={`text-sm ${isTodayDate ? 'text-primary font-bold' : ''}`}>
                  {day.getDate()}
                </CardTitle>
                <div className="flex space-x-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-4 w-4 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCopyScheduleItems(dateString);
                    }}
                    title="Copy day schedule"
                  >
                    <Copy className="h-2 w-2" />
                  </Button>
                  {copiedItems.length > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-4 w-4 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPasteScheduleItems(dateString);
                      }}
                      title={`Paste ${copiedItems.length} items`}
                    >
                      <Clipboard className="h-2 w-2" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-2 pt-0">
              <div 
                className="max-h-[70px] sm:max-h-[90px] md:max-h-[120px] overflow-y-auto relative"
              >
                <div className="grid grid-cols-2 gap-1">
                  {viewType === 'all' && visibleSchedules.map(item => {
                  const doctor = item.doctorId ? getDoctorById(item.doctorId) : null;
                  const staffMember = item.staffId ? getStaffById(item.staffId) : null;
                  const isLeave = item.type === 'leave' || item.type === 'mc';
                  const person = doctor || staffMember;
                  
                  if (!person) return null;
                  
                  return (
                    <div
                      key={item.id}
                      className={`text-xs p-1 rounded cursor-pointer hover:opacity-80 ${
                        item.type === 'mc'
                          ? 'bg-red-700 text-white'
                          : isLeave
                          ? 'bg-blue-200 text-blue-900'
                          : doctor ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                      }`}
                      style={{
                        backgroundColor: item.type === 'mc' ? '#b91c1c' : isLeave ? '#bfdbfe' : `${person.color}20`,
                        color: item.type === 'mc' ? '#ffffff' : isLeave ? '#1e3a8a' : person.color
                      }}
                      onClick={(e) => doctor ? handleDoctorClick(item, e) : handleStaffClick(item, e)}
                    >
                      <div className="font-medium truncate">
                        {person.short_name || person.name}
                      </div>
                    </div>
                  );
                })}
                
                {viewType === 'doctors' && (() => {
                  // Group schedules by doctor to show each doctor only once
                  const doctorGroups = daySchedules.reduce((groups, item) => {
                    if (item.doctorId) {
                      const doctorId = item.doctorId;
                      if (!groups[doctorId]) {
                        groups[doctorId] = [];
                      }
                      groups[doctorId].push(item);
                    }
                    return groups;
                  }, {} as Record<string, ScheduleItem[]>);
                  
                  const uniqueDoctors = Object.keys(doctorGroups);
                  
                  return uniqueDoctors.map(doctorId => {
                    const doctor = getDoctorById(doctorId);
                    const doctorSchedulesForDay = doctorGroups[doctorId];
                    
                    // Check if any of the doctor's schedules are leave/MC
                    const hasLeave = doctorSchedulesForDay.some(item => 
                      item.type === 'leave' || item.type === 'mc'
                    );
                    
                    // Use the first schedule item for the click handler (will show all in modal)
                    const primarySchedule = doctorSchedulesForDay[0];
                    
                    if (!doctor) return null;
                    
                    return (
                      <div
                        key={doctorId}
                        className={`text-xs p-1 rounded cursor-pointer hover:opacity-80 ${
                          doctorSchedulesForDay.some(item => item.type === 'mc')
                            ? 'bg-red-700 text-white'
                            : hasLeave
                            ? 'bg-blue-200 text-blue-900'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                        style={{
                          backgroundColor: doctorSchedulesForDay.some(item => item.type === 'mc') ? '#b91c1c' : hasLeave ? '#bfdbfe' : `${doctor.color}20`,
                          color: doctorSchedulesForDay.some(item => item.type === 'mc') ? '#ffffff' : hasLeave ? '#1e3a8a' : doctor.color
                        }}
                        onClick={(e) => handleDoctorClick(primarySchedule, e, doctorSchedulesForDay)}
                      >
                        <div className="font-medium truncate flex items-center justify-between">
                          <span>{doctor.short_name || doctor.name}</span>
                          {doctorSchedulesForDay.length > 1 && (
                            <span className="opacity-70">
                              ({doctorSchedulesForDay.length})
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
                
                {viewType === 'staff' && (() => {
                  // Group schedules by staff to show each staff only once
                  const staffGroups = daySchedules.reduce((groups, item) => {
                    if (item.staffId) {
                      const staffId = item.staffId;
                      if (!groups[staffId]) {
                        groups[staffId] = [];
                      }
                      groups[staffId].push(item);
                    }
                    return groups;
                  }, {} as Record<string, ScheduleItem[]>);
                  
                  const uniqueStaff = Object.keys(staffGroups);
                  
                  return uniqueStaff.map(staffId => {
                    const staffMember = getStaffById(staffId);
                    const staffSchedulesForDay = staffGroups[staffId];
                    
                    // Check if any of the staff's schedules are leave/MC
                    const hasLeave = staffSchedulesForDay.some(item => 
                      item.type === 'leave' || item.type === 'mc'
                    );
                    
                    // Use the first schedule item for the click handler (will show all in modal)
                    const primarySchedule = staffSchedulesForDay[0];
                    
                    if (!staffMember) return null;
                    
                    return (
                      <div
                        key={staffId}
                        className={`text-xs p-1 rounded cursor-pointer hover:opacity-80 ${
                          staffSchedulesForDay.some(item => item.type === 'mc')
                            ? 'bg-red-700 text-white'
                            : hasLeave
                            ? 'bg-blue-200 text-blue-900'
                            : 'bg-green-100 text-green-800'
                        }`}
                        style={{
                          backgroundColor: staffSchedulesForDay.some(item => item.type === 'mc') ? '#b91c1c' : hasLeave ? '#bfdbfe' : `${staffMember.color}20`,
                          color: staffSchedulesForDay.some(item => item.type === 'mc') ? '#ffffff' : hasLeave ? '#1e3a8a' : staffMember.color
                        }}
                        onClick={(e) => handleStaffClick(primarySchedule, e, staffSchedulesForDay)}
                      >
                        <div className="font-medium truncate flex items-center justify-between">
                          <span>{staffMember.short_name || staffMember.name}</span>
                          {staffSchedulesForDay.length > 1 && (
                            <span className="opacity-70">
                              ({staffSchedulesForDay.length})
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
                
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
      </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-3 sm:space-x-4">
          <h2 className="text-xl sm:text-2xl font-bold">{monthNames[month]} {year}</h2>
          <div className="flex space-x-1 sm:space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigateMonth('prev')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigateMonth('next')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0}>
                <Button
                  onClick={() => openScheduleModal()}
                  disabled={isAllClinicsView}
                  size="sm"
                >
                  <Plus className="h-4 w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Add Schedule</span>
                  <span className="sm:hidden">Add</span>
                </Button>
              </span>
            </TooltipTrigger>
            {isAllClinicsView && (
              <TooltipContent>
                <p>Please select a specific clinic to add schedules</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
          <div className="overflow-x-auto -mx-2 px-2 pb-1">
            <TabsList className="grid w-fit min-w-full sm:min-w-0 sm:w-fit grid-cols-4">
              <TabsTrigger value="planning" className="text-xs sm:text-sm whitespace-nowrap">Planning</TabsTrigger>
              <TabsTrigger value="all" className="text-xs sm:text-sm whitespace-nowrap">All</TabsTrigger>
              <TabsTrigger value="doctors" className="text-xs sm:text-sm whitespace-nowrap">Doctors</TabsTrigger>
              <TabsTrigger value="staff" className="text-xs sm:text-sm whitespace-nowrap">Staff</TabsTrigger>
            </TabsList>
          </div>

          <div className="flex items-center space-x-1 sm:space-x-2 overflow-x-auto pb-1">
            <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs shrink-0">Doctors</Badge>
            <Badge variant="outline" className="bg-green-50 text-green-700 text-xs shrink-0">Staff</Badge>
            <Badge variant="outline" className="bg-blue-200 text-blue-900 text-xs shrink-0">Leave</Badge>
            <Badge variant="outline" className="bg-red-700 text-white text-xs shrink-0">Urgent</Badge>
          </div>
        </div>

        <TabsContent value="planning">
          <PlanningMonthlyView
            calendarDays={calendarDays}
            scheduleItems={expandedScheduleItems}
            doctors={doctors}
            staff={staff}
            getDoctorById={getDoctorById}
            getStaffById={getStaffById}
            onDayClick={handleDayClick}
            onCopyScheduleItems={onCopyScheduleItems}
            onPasteScheduleItems={onPasteScheduleItems}
            copiedItems={copiedItems}
            onDoctorClick={handleDoctorClick}
            onStaffClick={handleStaffClick}
          />
        </TabsContent>

        <TabsContent value="all">
          {renderMonthlyContent('all')}
        </TabsContent>

        <TabsContent value="doctors">
          {renderMonthlyContent('doctors')}
        </TabsContent>

        <TabsContent value="staff">
          {renderMonthlyContent('staff')}
        </TabsContent>
      </Tabs>

      {/* Doctor Quick Report Modal */}
      <DoctorQuickReportModal
        isOpen={doctorModalState.isOpen}
        onClose={closeDoctorModal}
        scheduleItem={doctorModalState.scheduleItem}
        doctor={doctorModalState.doctor}
        staff={doctorModalState.staff}
        room={doctorModalState.room}
        onDeleteSchedule={handleDeleteSchedule}
        onEditSchedule={handleEditSchedule}
        allSchedulesForDay={doctorModalState.allSchedulesForDay}
        allStaff={staff}
        allRooms={rooms}
      />

      {/* Staff Quick Report Modal */}
      <StaffQuickReportModal
        isOpen={staffModalState.isOpen}
        onClose={closeStaffModal}
        scheduleItem={staffModalState.scheduleItem}
        staff={staffModalState.staff}
        doctor={staffModalState.doctor}
        room={staffModalState.room}
        onDeleteSchedule={handleDeleteSchedule}
        onEditSchedule={handleEditSchedule}
        allSchedulesForDay={staffModalState.allSchedulesForDay}
        allDoctors={doctors}
        allRooms={rooms}
      />

      {/* Schedule Modal */}
      <ScheduleModal
        isOpen={isScheduleModalOpen}
        onClose={closeScheduleModal}
        onSave={handleSaveSchedule}
        onDelete={handleDeleteSchedule}
        onDeleteSeries={handleDeleteSeries}
        onCreateAppointment={() => {}} // Empty handler for now
        doctors={doctors}
        staff={staff}
        rooms={rooms}
        initialData={editingScheduleItem}
        defaultStartTime="09:00"
        currentDate={getCurrentSingaporeDate()}
        scheduleItems={scheduleItems}
      />

      {/* Loading Overlay */}
      <LoadingOverlay
        isLoading={isLoadingSchedules || isTabLoading}
        message={isLoadingSchedules ? "Loading schedules..." : "Switching view..."}
      />
    </div>
  );
}
